import React from 'react';
import { Form, InputNumber, Row, Col, Card, Typography } from 'antd';
import {
    HomeOutlined,
    ApartmentOutlined,
    TeamOutlined
} from '@ant-design/icons';
import { useTranslation, Trans } from 'react-i18next';
import { useRouteFlags } from 'contexts/RouteProvider';

const { Title, Text } = Typography;

const ShortTermFields = ({
    listingData,
    handleChange,
    errors,
}) => {
    const { t } = useTranslation();
    const { shortTermListing } = useRouteFlags();

    // Handle InputNumber change (AntD returns value directly, not event)
    const handleNumberChange = (name) => (value) => {
        handleChange({
            target: {
                name,
                value: value || 0,
            },
        });
    };

    return (
        <div style={{ marginTop: '24px' }}>
            {/* Header */}
            <Title level={3} style={{ marginBottom: '24px' }}>
                <HomeOutlined style={{ marginRight: '12px', color: '#4d6765' }} />
                {t('createEditForm.headers.shortTermTechnical')}
            </Title>

            {/* Property Specifications Card */}
            <Card
                title={
                    <span style={{ fontSize: '16px', fontWeight: 600 }}>
                        <ApartmentOutlined style={{ marginRight: '8px', color: '#4d6765' }} />
                        {t("admin.listingsForms.propertySpecifications")}
                    </span>
                }
                style={{
                    marginBottom: '24px',
                    border: '2px solid #e8e8e8',
                    borderRadius: '8px',
                }}
            >
                <Row gutter={[24, 16]}>
                    {/* Floor Area - Full width or prominent */}
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={
                                <span style={{ fontSize: '14px', fontWeight: 500 }}>
                                    {t("propertyDetails.floorArea")} (m²)
                                </span>
                            }
                            validateStatus={errors?.floor_area ? "error" : ""}
                            help={errors?.floor_area?.[0]}
                            required
                        >
                            <InputNumber
                                name="floor_area"
                                value={listingData.floor_area}
                                onChange={handleNumberChange('floor_area')}
                                placeholder="e.g., 100"
                                min={0}
                                style={{ width: '100%' }}
                                size="large"
                            />
                        </Form.Item>
                    </Col>

                    {/* Floor */}
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={
                                <span style={{ fontSize: '14px', fontWeight: 500 }}>
                                    {t("propertyDetails.floor")}
                                </span>
                            }
                            validateStatus={errors?.floor ? "error" : ""}
                            help={errors?.floor?.[0]}
                        >
                            <InputNumber
                                name="floor"
                                value={listingData.floor || 0}
                                onChange={handleNumberChange('floor')}
                                placeholder="e.g., 2"
                                min={0}
                                style={{ width: '100%' }}
                                size="large"
                            />
                        </Form.Item>
                    </Col>

                    {/* Levels (only for non-short-term) */}
                    {!shortTermListing && (
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={
                                    <span style={{ fontSize: '14px', fontWeight: 500 }}>
                                        {t("propertyDetails.levels")}
                                    </span>
                                }
                                validateStatus={errors?.levels ? "error" : ""}
                                help={errors?.levels?.[0]}
                            >
                                <InputNumber
                                    name="levels"
                                    value={listingData.levels}
                                    onChange={handleNumberChange('levels')}
                                    placeholder="e.g., 1"
                                    min={0}
                                    style={{ width: '100%' }}
                                    size="large"
                                />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
            </Card>

            {/* Rooms & Facilities Card */}
            <Card
                title={
                    <span style={{ fontSize: '16px', fontWeight: 600 }}>
                        <HomeOutlined style={{ marginRight: '8px', color: '#4d6765' }} />
                        {t("admin.listingsForms.roomsAndFacilities")}
                    </span>
                }
                style={{
                    marginBottom: '24px',
                    border: '2px solid #e8e8e8',
                    borderRadius: '8px',
                }}
            >
                <Row gutter={[24, 16]}>
                    {/* Bedrooms */}
                    <Col xs={24} sm={12} lg={8}>
                        <Form.Item
                            label={
                                <span style={{ fontSize: '14px', fontWeight: 500 }}>
                                    {t("propertyDetails.bedrooms")}
                                </span>
                            }
                            validateStatus={errors?.bedrooms ? "error" : ""}
                            help={errors?.bedrooms?.[0]}
                            required
                        >
                            <InputNumber
                                name="bedrooms"
                                value={listingData.bedrooms}
                                onChange={handleNumberChange('bedrooms')}
                                placeholder="e.g., 3"
                                min={0}
                                style={{ width: '100%' }}
                                size="large"
                            />
                        </Form.Item>
                    </Col>

                    {/* Bathrooms */}
                    <Col xs={24} sm={12} lg={8}>
                        <Form.Item
                            label={
                                <span style={{ fontSize: '14px', fontWeight: 500 }}>
                                    {t("propertyDetails.bathrooms")}
                                </span>
                            }
                            validateStatus={errors?.bathrooms ? "error" : ""}
                            help={errors?.bathrooms?.[0]}
                            required
                        >
                            <InputNumber
                                name="bathrooms"
                                value={listingData.bathrooms}
                                onChange={handleNumberChange('bathrooms')}
                                placeholder="e.g., 2"
                                min={0}
                                style={{ width: '100%' }}
                                size="large"
                            />
                        </Form.Item>
                    </Col>

                    {/* WC */}
                    <Col xs={24} sm={12} lg={8}>
                        <Form.Item
                            label={
                                <span style={{ fontSize: '14px', fontWeight: 500 }}>
                                    {t("propertyDetails.wc")}
                                </span>
                            }
                            validateStatus={errors?.wc ? "error" : ""}
                            help={errors?.wc?.[0]}
                            tooltip="Guest toilets (separate from bathrooms)"
                        >
                            <InputNumber
                                name="wc"
                                value={listingData.wc}
                                onChange={handleNumberChange('wc')}
                                placeholder="e.g., 1"
                                min={0}
                                style={{ width: '100%' }}
                                size="large"
                            />
                        </Form.Item>
                    </Col>

                    {/* Kitchens */}
                    <Col xs={24} sm={12} lg={8}>
                        <Form.Item
                            label={
                                <span style={{ fontSize: '14px', fontWeight: 500 }}>
                                    {t("propertyDetails.kitchens")}
                                </span>
                            }
                            validateStatus={errors?.kitchens ? "error" : ""}
                            help={errors?.kitchens?.[0]}
                            required
                        >
                            <InputNumber
                                name="kitchens"
                                value={listingData.kitchens}
                                onChange={handleNumberChange('kitchens')}
                                placeholder="e.g., 1"
                                min={0}
                                style={{ width: '100%' }}
                                size="large"
                            />
                        </Form.Item>
                    </Col>

                    {/* Living Rooms */}
                    <Col xs={24} sm={12} lg={8}>
                        <Form.Item
                            label={
                                <span style={{ fontSize: '14px', fontWeight: 500 }}>
                                    {t("propertyDetails.living_rooms")}
                                </span>
                            }
                            validateStatus={errors?.living_rooms ? "error" : ""}
                            help={errors?.living_rooms?.[0]}
                            required
                        >
                            <InputNumber
                                name="living_rooms"
                                value={listingData.living_rooms}
                                onChange={handleNumberChange('living_rooms')}
                                placeholder="e.g., 1"
                                min={0}
                                style={{ width: '100%' }}
                                size="large"
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>

            {/* Guest Capacity Card - Only for Short-Term Listings */}
            {shortTermListing && (
                <Card
                    title={
                        <span style={{ fontSize: '16px', fontWeight: 600 }}>
                            <TeamOutlined style={{ marginRight: '8px', color: '#4d6765' }} />
                            {t("admin.listingsForms.guestCapacity")}
                        </span>
                    }
                    style={{
                        marginBottom: '24px',
                        border: '2px solid #e8e8e8',
                        borderRadius: '8px',
                        backgroundColor: '#f6ffed',
                    }}
                    styles={{ backgroundColor: '#ffffff' }}
                >
                    <div style={{
                        padding: '12px 16px',
                        backgroundColor: '#e6f4ff',
                        borderRadius: '6px',
                        marginBottom: '16px',
                        border: '1px solid #91caff'
                    }}>
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            <Trans i18nKey="admin.listingsForms.maxGuestsTip">{t("admin.listingsForms.maxGuestsTip")}</Trans>
                        </Text>
                    </div>

                    <Row gutter={[24, 16]}>
                        {/* Max Guests */}
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={
                                    <span style={{ fontSize: '14px', fontWeight: 500 }}>
                                        {t("propertyDetails.maxGuests")}
                                    </span>
                                }
                                validateStatus={errors?.max_guests ? "error" : ""}
                                help={errors?.max_guests?.[0]}
                                required
                            >
                                <InputNumber
                                    name="max_guests"
                                    value={listingData.max_guests}
                                    onChange={handleNumberChange('max_guests')}
                                    placeholder="e.g., 6"
                                    min={1}
                                    style={{ width: '100%' }}
                                    size="large"
                                />
                            </Form.Item>
                        </Col>

                        {/* Max Adults */}
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={
                                    <span style={{ fontSize: '14px', fontWeight: 500 }}>
                                        {t("propertyDetails.maxAdults")}
                                    </span>
                                }
                                validateStatus={errors?.max_adults ? "error" : ""}
                                help={errors?.max_adults?.[0]}
                                required
                            >
                                <InputNumber
                                    name="max_adults"
                                    value={listingData.max_adults}
                                    onChange={handleNumberChange('max_adults')}
                                    placeholder="e.g., 4"
                                    min={1}
                                    style={{ width: '100%' }}
                                    size="large"
                                />
                            </Form.Item>
                        </Col>

                        {/* Max Children */}
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={
                                    <span style={{ fontSize: '14px', fontWeight: 500 }}>
                                        {t("propertyDetails.maxChildren")}
                                    </span>
                                }
                                validateStatus={errors?.max_children ? "error" : ""}
                                help={errors?.max_children?.[0]}
                            >
                                <InputNumber
                                    name="max_children"
                                    value={listingData.max_children}
                                    onChange={handleNumberChange('max_children')}
                                    placeholder="e.g., 2"
                                    min={0}
                                    style={{ width: '100%' }}
                                    size="large"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Auto-calculation helper */}
                    {(listingData.max_adults || listingData.max_children) && (
                        <div style={{
                            marginTop: '8px',
                            padding: '8px 12px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '4px',
                            textAlign: 'center'
                        }}>
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                {t("admin.listingsForms.calculatedTotal")} <strong>{(listingData.max_adults || 0) + (listingData.max_children || 0)} {t("admin.listingsForms.guests")}</strong>
                                {listingData.max_guests !== ((listingData.max_adults || 0) + (listingData.max_children || 0)) && (
                                    <span style={{ color: '#faad14', marginLeft: '8px' }}>
                                        ⚠️ {t("admin.listingsForms.maxGuestsMismatch")}
                                    </span>
                                )}
                            </Text>
                        </div>
                    )}
                </Card>
            )}
        </div>
    );
};

export default ShortTermFields;